import React, { useState, useMemo, useEffect } from 'react';
import { string } from 'prop-types';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

import { intlShape, injectIntl } from '../../../util/reactIntl';
import {
  Button,
  Form,
  IconSearch,
  LocationAutocompleteInput,
  NamedLink,
  OutsideClickHandler,
} from '../../../components';

import css from './SearchForm.module.css';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { createResourceLocatorString } from '../../../util/routes';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { StaticEventType, StaticPrimaryCuisine } from '../../../util/enums';
import { constructQueryParamName, parseSelectFilterOptions } from '../../../util/search';

const normalizeOption = option =>
  typeof option === 'string'
    ? { option, label: option }
    : { option: option.option, label: option.label || option.option };

const getOptionLabel = (options, selected) =>
  options.find(option => option.option === selected)?.label || selected;

const getListingFieldParamName = (fieldConfig, fallback) =>
  fieldConfig ? constructQueryParamName(fieldConfig.key, fieldConfig.scope) : fallback;

const getSingleSelectParamValue = (params, paramNames) => {
  const paramName = paramNames.find(name => params.get(name));
  const paramValue = paramName ? params.get(paramName) : '';
  return paramValue ? parseSelectFilterOptions(paramValue)[0] : '';
};

const getSelectSearchValue = (fieldConfig, selectedOption) => {
  const searchMode = fieldConfig?.filterConfig?.searchMode;
  return searchMode ? `${searchMode}:${selectedOption}` : selectedOption;
};

const SearchFormComponent = props => {
  const { config } = props;
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const urlParams = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);

  const cuisineField = useMemo(
    () =>
      config?.listing?.listingFields?.find(
        field => field.key === 'primaryCuisine' || field.key === 'cuisines'
      ),
    [config]
  );
  const eventTypeField = useMemo(
    () =>
      config?.listing?.listingFields?.find(field => {
        const key = field.key?.toLowerCase() || '';
        const label = [
          field.filterConfig?.label,
          field.showConfig?.label,
          field.saveConfig?.label,
          field.label,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return key === 'eventtype' || key === 'eventtypes' || label.includes('event type');
      }),
    [config]
  );
  const cuisineParamName = getListingFieldParamName(cuisineField, 'pub_cuisine');
  const eventTypeParamName = getListingFieldParamName(eventTypeField, 'pub_eventType');
  const selectedCuisineFromURL = getSingleSelectParamValue(urlParams, [
    cuisineParamName,
    'pub_cuisine',
  ]);
  const selectedEventTypeFromURL = getSingleSelectParamValue(urlParams, [
    eventTypeParamName,
    'pub_eventType',
  ]);

  const [showCuisine, setShowCuisine] = useState(false);
  const [showEventType, setShowEventType] = useState(false);
  const [selectedCuisine, setSelectedCuisine] = useState(selectedCuisineFromURL);
  const [selectedEventType, setSelectedEventType] = useState(selectedEventTypeFromURL);

  const cuisineOptions = useMemo(() => {
    if (!cuisineField?.enumOptions || cuisineField.enumOptions.length === 0) {
      return StaticPrimaryCuisine.map(normalizeOption);
    }

    const options = cuisineField.enumOptions.map(normalizeOption);
    return [...options];
  }, [cuisineField]);

  const eventTypeOptions = useMemo(() => {
    if (!eventTypeField?.enumOptions || eventTypeField.enumOptions.length === 0) {
      return StaticEventType.map(normalizeOption);
    }

    return eventTypeField.enumOptions.map(normalizeOption);
  }, [eventTypeField]);

  useEffect(() => {
    setSelectedCuisine(selectedCuisineFromURL);
    setSelectedEventType(selectedEventTypeFromURL);
  }, [selectedCuisineFromURL, selectedEventTypeFromURL]);

  const handleSubmit = values => {
    const query = {};

    // Location
    if (values.location?.selectedPlace) {
      const place = values.location.selectedPlace;
      const address = place.address || values.location.search || '';
      query.address = address;
      query.bounds = place.bounds;
    }

    // Cuisine
    if (selectedCuisine) {
      query[cuisineParamName] = getSelectSearchValue(cuisineField, selectedCuisine);
    }

    // Event Type
    if (selectedEventType && selectedEventType !== 'Any') {
      query[eventTypeParamName] = getSelectSearchValue(eventTypeField, selectedEventType);
    }

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, query));
  };

  return (
    <FinalForm
      onSubmit={handleSubmit}
      initialValues={{
        location: urlParams.get('address') ? { search: urlParams.get('address') } : null,
      }}
      {...props}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          viewport,
          isSearchPageFilter,
          isLandingPage,
          isMobileSearchOpen,
          intl,
          handleSubmit: finalFormHandleSubmit,
          form,
        } = formRenderProps;

        const isMobileLayout = viewport && viewport?.width < 1024;
        const search = location?.search || '';

        const handleClearFilters = () => {
          setSelectedCuisine('');
          setSelectedEventType('');
          setShowCuisine(false);
          setShowEventType(false);

          form.reset({ location: null });
          history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, {}));
        };

        const classes = classNames(rootClassName || css.root, className, {
          [css.searchPageFilter]: isSearchPageFilter,
          [css.mobileSearchOpen]: isMobileSearchOpen,
        });

        return (
          <Form className={classes} onSubmit={finalFormHandleSubmit}>
            {/* Location  */}
            <LocationSearchField
              intl={intl}
              handleChange={value => {
                if (value?.selectedPlace) form.change('location', value);
              }}
              isLandingLocation={!isSearchPageFilter}
              isSearchPageFilter={isSearchPageFilter}
            />

            {/* Cuisine  */}
            <OutsideClickHandler
              className={css.travelDateSection}
              onOutsideClick={() => setShowCuisine(false)}
            >
              <label htmlFor="cuisine">Cuisine</label>
              <span
                className={css.guestsBtn}
                onClick={() => setShowCuisine(!showCuisine)}
                role="button"
                tabIndex={0}
                onKeyDown={e =>
                  (e.key === 'Enter' || e.key === ' ') && setShowCuisine(!showCuisine)
                }
              >
                {selectedCuisine
                  ? getOptionLabel(cuisineOptions, selectedCuisine)
                  : 'Select Cuisine'}
              </span>

              {showCuisine && (
                <div className={css.dropdownWrapper}>
                  <CuisineDropdown
                    options={cuisineOptions}
                    selectedCuisine={selectedCuisine}
                    onSelect={cuisine => {
                      setSelectedCuisine(cuisine === 'Any' ? '' : cuisine);
                      setShowCuisine(false);
                    }}
                  />
                </div>
              )}
            </OutsideClickHandler>

            {/* Event Type  */}
            <OutsideClickHandler
              className={css.guestsWrapper}
              onOutsideClick={() => setShowEventType(false)}
            >
              <label htmlFor="eventType">Event Type</label>
              <span
                onClick={() => setShowEventType(!showEventType)}
                className={css.guestsBtn}
                role="button"
                tabIndex={0}
                onKeyDown={e =>
                  (e.key === 'Enter' || e.key === ' ') && setShowEventType(!showEventType)
                }
              >
                {selectedEventType
                  ? getOptionLabel(eventTypeOptions, selectedEventType)
                  : 'Select Event Type'}
              </span>

              {showEventType && (
                <div className={css.dropdownWrapper}>
                  <EventTypeDropdown
                    options={eventTypeOptions}
                    selectedEventType={selectedEventType}
                    onSelect={eventType => {
                      setSelectedEventType(eventType === 'Any' ? '' : eventType);
                      setShowEventType(false);
                    }}
                  />
                </div>
              )}
            </OutsideClickHandler>

            {/* Clear Filters & Search Button */}
            {search?.length > 1 && !isMobileLayout && (
              <div
                className={css.clearFilter}
                onClick={handleClearFilters}
                role="button"
                tabIndex={0}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleClearFilters()}
              >
                &times;
              </div>
            )}

            <div className={classNames(isLandingPage ? css.fullWidthBtn : css.footerBtn)}>
              {search?.length > 1 && isMobileLayout && (
                <NamedLink
                  className={css.clearFilter}
                  name="SearchPage"
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </NamedLink>
              )}

              <Button
                className={classNames({
                  [css.searchIcon]: isSearchPageFilter,
                  [css.searchBtn]: !isSearchPageFilter,
                })}
                type="submit"
              >
                {isMobileLayout ? (
                  <span className={css.landingSearchBtn}>Search</span>
                ) : (
                  <IconSearch />
                )}
              </Button>
            </div>
          </Form>
        );
      }}
    />
  );
};

// Dropdown Components
const GenericDropdown = ({ options, selected, onSelect }) => (
  <div className={css.dropdown}>
    {options.map(option => (
      <div
        key={option.option}
        className={classNames(css.dropdownItem, {
          [css.selected]: selected === option.option,
        })}
        onClick={() => onSelect(option.option)}
      >
        {option.label}
      </div>
    ))}
  </div>
);

const CuisineDropdown = ({ options, selectedCuisine, onSelect }) => (
  <GenericDropdown options={options} selected={selectedCuisine} onSelect={onSelect} />
);

const EventTypeDropdown = ({ options, selectedEventType, onSelect }) => (
  <GenericDropdown options={options} selected={selectedEventType} onSelect={onSelect} />
);

const LocationSearchField = ({ intl, handleChange, isLandingLocation, isSearchPageFilter }) => (
  <Field
    name="location"
    format={v => v}
    render={({ input, meta }) => {
      const { onChange, ...restInput } = input;
      const searchOnChange = value => {
        onChange(value);
        handleChange(value);
      };
      return (
        <LocationAutocompleteInput
          label="Location"
          placeholder="Indiranagar Bengaluru"
          iconClassName={css.searchInputIcon}
          inputClassName={css.searchInput}
          predictionsClassName={css.searchPredictions}
          input={{ ...restInput, onChange: searchOnChange }}
          meta={meta}
          rootClassName={css.landingLocationSearch}
          isLandingLocation={isLandingLocation}
          isSearchPageFilter={isSearchPageFilter}
          useDarkText={isSearchPageFilter}
        />
      );
    }}
  />
);

SearchFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  intl: intlShape.isRequired,
};

const SearchForm = injectIntl(SearchFormComponent);
export default SearchForm;
